import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Activity, ShieldCheck } from "lucide-react";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ password: "", full_name: "" });

  useEffect(() => {
    if (!token) { setErr("Missing invite token"); return; }
    api.get(`/team/invite/${token}`)
      .then(r => setInvite(r.data))
      .catch(e => setErr(formatApiError(e.response?.data?.detail) || e.message));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post("/team/accept", { token, ...form });
      await refresh();
      nav("/app/dashboard");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--navy-900)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div className="card" style={{ padding: 32, maxWidth: 460, width: "100%" }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24, textDecoration: "none" }}>
          <div style={{ width: 32, height: 32, background: "var(--amber-500)", display: "grid", placeItems: "center", borderRadius: 2 }}>
            <Activity size={18} color="var(--navy-900)" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-serif-display" style={{ fontSize: 16, fontWeight: 700, color: "var(--ink)", lineHeight: 1 }}>CanadaMRO</div>
            <div className="font-mono" style={{ fontSize: 10, color: "var(--ink-mute)", letterSpacing: ".22em" }}>EXCHANGE</div>
          </div>
        </Link>

        {err && !invite && <div style={{ color: "var(--red)", padding: 14, border: "1px solid var(--red)", borderRadius: 4 }}>{err}</div>}

        {invite && (
          <>
            <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--amber-400)", marginBottom: 6 }}>
              <ShieldCheck size={12} style={{ display: "inline-block", marginRight: 6, verticalAlign: -2 }} />TEAM INVITATION
            </div>
            <h1 className="font-serif-display" style={{ fontSize: 26, fontWeight: 600, marginBottom: 12, color: "var(--ink)" }}>
              You're invited to join<br/>{invite.company_name}
            </h1>
            <p style={{ color: "var(--ink-dim)", fontSize: 14, marginBottom: 20 }}>
              <b>{invite.invited_by}</b> invited <b>{invite.email}</b> to join <b>{invite.company_name}</b> ({invite.company_role}) as a <b style={{ color: "var(--amber-400)" }}>{invite.role}</b>.
            </p>
            {invite.message && (
              <div style={{ padding: 12, background: "var(--navy-900)", borderLeft: "2px solid var(--amber-500)", marginBottom: 20, fontSize: 13, color: "var(--ink-dim)", fontStyle: "italic" }}>
                "{invite.message}"
              </div>
            )}

            <form onSubmit={submit} style={{ display: "grid", gap: 14 }}>
              <div>
                <label className="label">Your full name *</label>
                <input data-testid="accept-name" className="input" required value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
              </div>
              <div>
                <label className="label">Choose a password *</label>
                <input data-testid="accept-password" className="input" type="password" required minLength={6} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="At least 6 characters" />
              </div>
              {err && <div style={{ color: "var(--red)", fontSize: 13 }}>{err}</div>}
              <button data-testid="accept-submit" disabled={busy} className="btn btn-primary" style={{ justifyContent: "center", padding: "12px" }}>
                {busy ? "Setting up your account…" : `Accept and join ${invite.company_name}`}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
